import type { QueryClient } from '@tanstack/react-query';
import {
  handleGetGeometryGeometriesGeometryIdGetOptions,
  handleGetLineageGeometriesGeometryIdLineageGetOptions,
  handleListGeometryMeshesGeometriesGeometryIdMeshesGetOptions,
  handleDownloadDiagnosisGeometriesGeometryIdDiagnosisDownloadGetOptions,
} from '@/api/generated/@tanstack/react-query.gen';
import { diagnosisResultQueryOptions } from '@/hooks/useDiagnosisStatus';
import { UUID_REGEX } from '@/lib/uuid';

interface MeshGenLoaderArgs {
  context: { queryClient: QueryClient };
  params: { uuid: string };
}

/**
 * `/meshgen/$uuid` route loader — warms a geometry's meshes + diagnosis into
 * the query cache so opening a geometry lands the workspace on content instead
 * of spinners. It runs on the dashboard row click (navigation) and, for the
 * grid-view cards (which are `<Link>`s), on hover-intent preload. List-view
 * rows navigate programmatically, so there it runs at click time only.
 *
 * Shares the SAME generated query options as the workspace hooks
 * (`useMeshGenTree`, `useGeometryStatus`, `useGeometryLineage`,
 * `useDiagnosisStatus`) so a warmed entry is reused, not refetched, once
 * `MeshGenWorkspace` mounts.
 *
 * Best-effort: every warm is `.catch`'d so a failure (404, still-diagnosing,
 * network) falls through to the component's own `useQuery` retry/error UI
 * rather than the route error boundary, same contract as the list loaders.
 */
export async function meshGenWorkspaceLoader({
  context: { queryClient },
  params: { uuid },
}: MeshGenLoaderArgs): Promise<void> {
  // Guard invalid ids: the component renders GeometryNotFound for these, so
  // firing API calls would only 404.
  if (!UUID_REGEX.test(uuid)) return;

  const path = { geometry_id: uuid };

  // Two distinct mesh-list cache entries, both read by the workspace:
  //  - full lineage list (no limit)  -> useMeshGenTree (sidebar tree)
  //  - latest mesh (limit: 1)        -> useGeometryStatus + useInitialProjectLoad
  //                                     (status panel, viewer, first paint)
  // The generated query key includes the full `query` object, so these are
  // separate entries; warm both or the status query still loads cold on mount.
  const meshListFull = queryClient
    .ensureQueryData(
      handleListGeometryMeshesGeometriesGeometryIdMeshesGetOptions({
        path,
        query: { lineage: true },
      }),
    )
    .catch(() => undefined);

  const meshListLatest = queryClient
    .ensureQueryData(
      handleListGeometryMeshesGeometriesGeometryIdMeshesGetOptions({
        path,
        query: { limit: 1, lineage: true },
      }),
    )
    .catch(() => undefined);

  const lineage = queryClient
    .ensureQueryData(
      handleGetLineageGeometriesGeometryIdLineageGetOptions({ path }),
    )
    .catch(() => undefined);

  // Geometry detail carries the inline diagnosis summary + `diagnosis_at`, and
  // gates the diagnosis fetch below. `retry: false` fails fast on a 404 so a
  // missing/inaccessible geometry doesn't stall navigation (the component
  // surfaces not-found on its own).
  const geometry = await queryClient
    .ensureQueryData({
      ...handleGetGeometryGeometriesGeometryIdGetOptions({ path }),
      retry: false,
    })
    .catch(() => undefined);

  // The full diagnosis result is a potentially large S3 blob. Kick the fetch
  // off now so the click/hover starts warming it, but DO NOT block navigation
  // on it: awaiting it would hold the route behind the pending spinner until
  // the blob downloads (and hang if S3 stalls). `useDiagnosisStatus` reads the
  // same cache entry once the workspace mounts and picks up the in-flight
  // result. The diagnosis summary needed for first paint is already on the
  // (awaited) geometry detail above. Hoisted to a const so the narrowing holds
  // inside the `.then` closure.
  const diagnosisAt = geometry?.diagnosis_at;
  if (diagnosisAt) {
    void queryClient
      .ensureQueryData(
        handleDownloadDiagnosisGeometriesGeometryIdDiagnosisDownloadGetOptions({
          path,
        }),
      )
      .then((dl) =>
        dl?.download_url
          ? queryClient.ensureQueryData(
              diagnosisResultQueryOptions(uuid, diagnosisAt, dl.download_url),
            )
          : undefined,
      )
      .catch(() => undefined);
  }

  // Block navigation only on the light, fast warms.
  await Promise.all([meshListFull, meshListLatest, lineage]);
}
